import { Check, Minus, PencilSimpleLine, Plus, X } from "@phosphor-icons/react";
import { useState } from "react";

import { Button } from "common/components/ui/Button";
import Progress from "common/components/ui/Progress";
import { TableCell, TableRow } from "common/components/ui/TableV2";
import WorkspaceImage from "common/components/WorkspaceImage";
import usePromotions from "common/datahooks/usePromotions";
import useNumberSpinner from "common/hooks/useNumberSpinner";
import type { Workspace } from "common/types";

export default function WorkspacesTableRow({
  workspace,
  isEditing,
  toggleIsEditing,
}: {
  workspace: Workspace;
  isEditing: boolean;
  toggleIsEditing: () => void;
}) {
  const { agencyPlan } = usePromotions();
  const { id, lifetime_license_count, accounts_count, name, icon_url } =
    workspace;

  const [seatCount, setSeatCount] = useState(lifetime_license_count);

  const { inputProps, incrementButtonProps, decrementButtonProps } =
    useNumberSpinner(
      seatCount,
      setSeatCount,
      0,
      agencyPlan.unassigned_license_count,
    );

  return (
    <TableRow key={id}>
      <TableCell>
        <div className="flex gap-x-2.5 items-center">
          <WorkspaceImage id={id} imageSrc={icon_url} />
          <span className="text-md-bold">{name}</span>
        </div>
      </TableCell>

      <TableCell className="text-xs-bold">{accounts_count}</TableCell>

      <TableCell className="text-button-xs">
        <Progress
          value={
            accounts_count ? (accounts_count / lifetime_license_count) * 100 : 0
          }
          className="w-16"
        />
      </TableCell>

      <TableCell className="w-28">
        {isEditing ? (
          <div className="flex bg-black-50 px-2 py-0.5 rounded-xl gap-x-1 items-center text-black-950 hover:bg-black-100 w-fit has-[:focus-visible]:bg-black-100">
            <button
              type="button"
              className="disabled:text-black-300"
              {...decrementButtonProps}
            >
              <Minus className="size-4" />
            </button>
            <input
              className="w-10 h-7 bg-white rounded-lg p-2 text-center text-button-xs focus-visible:border-purple-500 border focus-visible:outline-none border-white"
              {...inputProps}
            />
            <button
              type="button"
              className="disabled:text-black-300"
              {...incrementButtonProps}
            >
              <Plus className="size-4" />
            </button>
          </div>
        ) : (
          <span className="text-button-xs text-black-500">
            {lifetime_license_count}
          </span>
        )}
      </TableCell>

      <TableCell>
        {isEditing ? (
          <div className="flex gap-x-2 justify-end">
            <Button
              variant="secondary-black"
              size="sm"
              intent="iconOnly"
              onClick={() => {
                toggleIsEditing();
                //reset state on cancel
                setSeatCount(lifetime_license_count);
              }}
            >
              <X />
            </Button>
            <Button
              variant="secondary-purple"
              size="sm"
              intent="iconOnly"
              onClick={toggleIsEditing}
            >
              <Check />
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost-black"
            size="sm"
            intent="iconOnly"
            onClick={toggleIsEditing}
          >
            <PencilSimpleLine />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
